import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import './RegisterOvi.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Alert from '../../components/Alert/Alert';
import latinCountries from '../../utils/Countries/latin-america-countries.json';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Flag from 'react-world-flags';

const options = latinCountries.map((country) => ({
    label: (
        <div className="country-option">
            <Flag code={country.value} style={{ width: 20, height: 15, borderRadius: 2 }} />
            <span>{country.label}</span>
        </div>
    ),
    value: country.value,
    code: country.dial_code,
    example: country.example,
}));

const checkIfPhoneExists = async (numeroLocal) => {
    try {
        const res = await axios.get(`https://api.minymol.com/users/resolve-phone/${numeroLocal}`);
        return res.data.phoneNumber;
    } catch (err) {
        return null;
    }
};

const RegisterOvi = () => {
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [filteredCities, setFilteredCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(
        options.find(opt => opt.value === 'CO')
    );
    const [localCell, setLocalCell] = useState('');
    const [internationalCell, setInternationalCell] = useState('');

    const [alertType, setAlertType] = useState(null);
    const [alertMessage, setAlertMessage] = useState('');
    const [showAlert, setShowAlert] = useState(false);

    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [showPass, setShowPass] = useState(false);
    const [showRepeat, setShowRepeat] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);

    const [formData, setFormData] = useState({
        password: '',
        repeatPassword: '',
        firstName: '',
        lastName: '',
        cell: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const passwordValidation = {
        hasLetter: /[a-zA-Z]/.test(formData.password),
        hasNumberOrSymbol: /[\d\W]/.test(formData.password),
        hasMinLength: formData.password.length >= 10,
    };

    const nextStep = async () => {
        if (step === 1) {
            if (!selectedCountry || !localCell.trim()) {
                setAlertType('error');
                setAlertMessage('Selecciona un país e ingresa un número de celular.');
                setShowAlert(true);
                return;
            }

            const fullNumber = selectedCountry.dial_code + localCell.replace(/^0+/, '');
            const parsed = parsePhoneNumberFromString(fullNumber, selectedCountry.value);

            if (!parsed || !parsed.isValid()) {
                setAlertType('error');
                setAlertMessage('Número de celular inválido para el país seleccionado.');
                setShowAlert(true);
                return;
            }

            const e164 = parsed.number;
            setInternationalCell(e164);
            const local = localCell;

            try {
                const existe = await checkIfPhoneExists(local);
                if (existe) {
                    setAlertType('error');
                    setAlertMessage('Ese número ya está registrado. Inicia sesión.');
                    setShowAlert(true);
                    return;
                }

                setFormData((prev) => ({ ...prev, cell: local }));
                setStep(2);
            } catch (error) {
                setAlertType('error');
                setAlertMessage('No se pudo verificar el número.');
                setShowAlert(true);
            }
            return;
        }

        if (step === 2) {
            if (!formData.password.trim() || !formData.repeatPassword.trim()) {
                setAlertType('error');
                setAlertMessage('Debes completar ambos campos de contraseña.');
                setShowAlert(true);
                return;
            }

            if (formData.password !== formData.repeatPassword) {
                setAlertType('error');
                setAlertMessage('Las contraseñas no coinciden.');
                setShowAlert(true);
                return;
            }

            if (!passwordValidation.hasLetter || !passwordValidation.hasNumberOrSymbol || !passwordValidation.hasMinLength) {
                setAlertType('error');
                setAlertMessage('La contraseña no cumple con los requisitos.');
                setShowAlert(true);
                return;
            }
        }

        setStep(step + 1);
    };

    const prevStep = () => {
        setStep(step - 1);
    };

    const sendTokenToBackend = async (token) => {
        try {
            const response = await axios.post(
                'https://api.minymol.com/auth/login',
                { token }
            );

            const userData = response.data;
            localStorage.setItem('usuario', JSON.stringify(userData));
            localStorage.setItem('token', token);

            navigate('/my-products');
        } catch (error) {
            console.error('Error desde el backend:', error);
            setAlertType('error');
            setAlertMessage('No se pudo autenticar con el servidor');
            setShowAlert(true);
        }
    };

    const completeUserProfile = async (token) => {
        try {
            await axios.post(
                'https://api.minymol.com/users/complete-profile',
                {
                    nombre: `${formData.firstName} ${formData.lastName}`,
                    telefono: internationalCell,
                    departamento: selectedDepartment,
                    ciudad: selectedCity,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            console.log('✅ Perfil completado');
        } catch (error) {
            console.error('Error al completar el perfil:', error);
        }
    };

    useEffect(() => {
        if (step === 3 && departments.length === 0) {
            axios.get('https://raw.githubusercontent.com/marcovega/colombia-json/master/colombia.json')
                .then((res) => {
                    setDepartments(res.data);
                })
                .catch((err) => {
                    console.error('Error al cargar ciudades:', err);
                });
        }
    }, [step, departments.length]);

    const handleDepartmentChange = (selected) => {
        const dept = selected?.value;
        setSelectedDepartment(dept);
        setSelectedCity(null);

        const selectedDept = departments.find((d) => d.departamento === dept);
        if (selectedDept) {
            setFilteredCities(selectedDept.ciudades.map(c => ({ value: c, label: c })));
        } else {
            setFilteredCities([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !formData.firstName.trim() ||
            !formData.lastName.trim() ||
            !formData.cell.trim() ||
            !selectedDepartment ||
            !selectedCity
        ) {
            setAlertType('error');
            setAlertMessage('Todos los campos son obligatorios.');
            setShowAlert(true);
            return;
        }

        setIsRegistering(true);

        try {
            const fakeEmail = formData.cell.trim() + '@minymol.com';
            const result = await createUserWithEmailAndPassword(auth, fakeEmail, formData.password);

            await updateProfile(result.user, {
                displayName: `${formData.firstName} ${formData.lastName}`
            });

            await result.user.reload();
            const token = await result.user.getIdToken(true);

            await sendTokenToBackend(token);
            await completeUserProfile(token);

            setAlertType('success');
            setAlertMessage('Registro exitoso.');
            setShowAlert(true);
        } catch (err) {
            console.error('Error al registrar:', err);
            setAlertType('error');
            setAlertMessage('Hubo un error al registrar el usuario');
            setShowAlert(true);
        } finally {
            setIsRegistering(false);
        }
    };

    return (
        <div className="registerovi-container">
            {showAlert && (
                <Alert
                    type={alertType}
                    message={alertMessage}
                    onClose={() => setShowAlert(false)}
                />
            )}

            <div className="registerovi-gradient-bg"></div>

            <div className="registerovi-card">
                <div className="registerovi-header">
                    <div className="registerovi-logo">
                        <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <linearGradient id="ovi-register-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#4169E1" />
                                    <stop offset="50%" stopColor="#8B5CF6" />
                                    <stop offset="100%" stopColor="#10B981" />
                                </linearGradient>
                            </defs>
                            <circle cx="30" cy="30" r="25" fill="none" stroke="url(#ovi-register-gradient)" strokeWidth="3" opacity="0.3" />
                            <path d="M 18 24 L 18 36 Q 18 39 21 39 L 28.5 39 L 28.5 21 L 21 21 Q 18 21 18 24 Z"
                                fill="url(#ovi-register-gradient)" />
                            <path d="M 31.5 21 L 31.5 39 L 39 39 Q 42 39 42 36 L 42 24 Q 42 21 39 21 Z"
                                fill="url(#ovi-register-gradient)" opacity="0.8" />
                            <line x1="30" y1="21" x2="30" y2="39" stroke="#ffffff" strokeWidth="1" opacity="0.3" />
                        </svg>
                    </div>
                    <h1 className="registerovi-title">OviCatalogos</h1>
                    <p className="registerovi-subtitle">
                        {step === 1 && 'Crear tu cuenta'}
                        {step === 2 && 'Elige tu contraseña'}
                        {step === 3 && 'Información personal'}
                    </p>
                    <div className="registerovi-steps">
                        <div className={`registerovi-step ${step >= 1 ? 'active' : ''}`}></div>
                        <div className={`registerovi-step ${step >= 2 ? 'active' : ''}`}></div>
                        <div className={`registerovi-step ${step >= 3 ? 'active' : ''}`}></div>
                    </div>
                </div>

                <form className="registerovi-form">
                    {step === 1 && (
                        <div className="registerovi-step-content">
                            <div className="registerovi-input-group">
                                <label className="registerovi-label">País</label>
                                <Select
                                    options={options}
                                    placeholder="Selecciona tu país..."
                                    value={selectedCountry}
                                    onChange={setSelectedCountry}
                                    isSearchable
                                    classNamePrefix="ovi-select"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '12px',
                                            border: '2px solid #E2E8F0',
                                            padding: '4px',
                                        }),
                                    }}
                                />
                            </div>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Número de celular</label>
                                <div className="registerovi-phone-input">
                                    <span className="registerovi-country-code">
                                        {selectedCountry ? selectedCountry.code : '+57'}
                                    </span>
                                    <input
                                        type="text"
                                        placeholder={`Ej.: ${selectedCountry?.example || '0912345678'}`}
                                        value={localCell}
                                        onChange={(e) => setLocalCell(e.target.value)}
                                        className="registerovi-input"
                                    />
                                </div>
                            </div>

                            <button type="button" onClick={nextStep} className="registerovi-btn-submit">
                                Continuar
                            </button>

                            <div className="registerovi-footer">
                                <p className="registerovi-footer-text">
                                    ¿Ya tienes cuenta?{' '}
                                    <a href="/loginovi" className="registerovi-link">Inicia sesión</a>
                                </p>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="registerovi-step-content">
                            <button type="button" onClick={prevStep} className="registerovi-back-btn">
                                <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                            </button>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Contraseña</label>
                                <div className="registerovi-password-wrapper">
                                    <input
                                        type={showPass ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Ingrese la contraseña"
                                        className="registerovi-input"
                                    />
                                    <span className="registerovi-toggle-pass" onClick={() => setShowPass(!showPass)}>
                                        <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                            </div>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Repetir contraseña</label>
                                <div className="registerovi-password-wrapper">
                                    <input
                                        type={showRepeat ? 'text' : 'password'}
                                        name="repeatPassword"
                                        value={formData.repeatPassword}
                                        onChange={handleChange}
                                        placeholder="Repita la contraseña"
                                        className="registerovi-input"
                                    />
                                    <span className="registerovi-toggle-pass" onClick={() => setShowRepeat(!showRepeat)}>
                                        <FontAwesomeIcon icon={showRepeat ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                            </div>

                            <div className="registerovi-password-checklist">
                                <p className={passwordValidation.hasLetter ? 'valid' : 'invalid'}>
                                    {passwordValidation.hasLetter ? '✓' : '○'} Al menos 1 letra
                                </p>
                                <p className={passwordValidation.hasNumberOrSymbol ? 'valid' : 'invalid'}>
                                    {passwordValidation.hasNumberOrSymbol ? '✓' : '○'} Número o carácter especial
                                </p>
                                <p className={passwordValidation.hasMinLength ? 'valid' : 'invalid'}>
                                    {passwordValidation.hasMinLength ? '✓' : '○'} Mínimo 10 caracteres
                                </p>
                            </div>

                            <button type="button" onClick={nextStep} className="registerovi-btn-submit">
                                Siguiente
                            </button>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="registerovi-step-content">
                            <button type="button" onClick={prevStep} className="registerovi-back-btn">
                                <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                            </button>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Nombre</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder="Ej.: Juan"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="registerovi-input"
                                />
                            </div>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Apellido</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder="Ej.: Pérez"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="registerovi-input"
                                />
                            </div>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Departamento</label>
                                <Select
                                    options={departments.map(dep => ({ value: dep.departamento, label: dep.departamento }))}
                                    placeholder="Selecciona tu departamento..."
                                    value={selectedDepartment ? { value: selectedDepartment, label: selectedDepartment } : null}
                                    onChange={handleDepartmentChange}
                                    isClearable
                                    classNamePrefix="ovi-select"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '12px',
                                            border: '2px solid #E2E8F0',
                                            padding: '4px',
                                        }),
                                    }}
                                />
                            </div>

                            <div className="registerovi-input-group">
                                <label className="registerovi-label">Ciudad</label>
                                <Select
                                    options={filteredCities}
                                    placeholder="Selecciona tu ciudad..."
                                    value={selectedCity ? { value: selectedCity, label: selectedCity } : null}
                                    onChange={(selected) => setSelectedCity(selected?.value || null)}
                                    isClearable
                                    classNamePrefix="ovi-select"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '12px',
                                            border: '2px solid #E2E8F0',
                                            padding: '4px',
                                        }),
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isRegistering}
                                className="registerovi-btn-submit"
                                style={{
                                    opacity: isRegistering ? 0.7 : 1,
                                    cursor: isRegistering ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isRegistering ? 'Registrando...' : 'Crear cuenta'}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default RegisterOvi;
